// =============================================================================
// RoutineExecutor — roll, select hits, show summary
// =============================================================================
import { useState, useMemo, useEffect, useRef } from 'react';
import { Swords, ToggleLeft, ToggleRight, Copy, Check, Loader2, X, CheckCircle2 } from 'lucide-react';
import type {
  AttackRoutine, CombatModifierWithStates,
  PendingRollResult, CompletedRollResult, RollMode,
  AttackNode, AttackRollResult, BonusAttackNode, SpecialDamageType, WeaponSpecialEffectResult,
} from '../../../../backend/shared/types/index';
import { SPECIAL_DAMAGE_LABELS } from '../../../../backend/shared/types/index';
import { rollPhaseA, rollPhaseC, rollOneBonusAttack } from '@/engine/rollEngine';
import { formatForDiscord, formatForClipboard } from '@/utils/formatRollResult';
import { activeModifiersForCharacter, combatModifierTargets, resolveStackedBonus } from '@/utils/stackingResolver';
import { buildModifierCalculations, formatCalculationTerm } from '@/utils/modifierCalculation';
import { adjustDiceForSizeChange, sizeStepLabel, WEAPON_SIZES, type WeaponSize } from '@/engine/sizeProgression';
import { BUILT_IN_BONUS_TYPES } from '../../../../backend/shared/config/bonusTypes';
import { apiFetch } from '@/lib/api';
import { useAppSelector } from '@/store/hooks';

function resolveFlat(
  modifiers: CombatModifierWithStates[],
  characterId: string,
  targetAttrs: Set<string>,
): number {
  const bonusTypeMap = Object.fromEntries(BUILT_IN_BONUS_TYPES.map((t) => [t.typeId, t]));
  const active = activeModifiersForCharacter(modifiers, characterId);

  // Collect numeric effects matching the target attributes (skip roll modifiers).
  const effects = active.flatMap((m) =>
    m.effects.filter((e) => !e.rollModifier && e.bonusTypeId && e.value !== undefined && targetAttrs.has(e.targetAttribute)),
  );

  return resolveStackedBonus(effects, bonusTypeMap);
}

// ---------------------------------------------------------------------------

type Phase = 'idle' | 'rolled' | 'bonus_round' | 'resolved';

/** One rolled attack for a bonus round, tagged with the source node */
interface BonusRoundEntry {
  nodeId:    string;
  nodeLabel: string;
  atk:       AttackRollResult;
}

/** Resolved outcome of one confirmed bonus round */
interface ResolvedBonusRound {
  roundNum:            number;
  entries:             Array<BonusRoundEntry & { isHit: boolean }>;
  roundDamage:         number;  // HP damage only
  roundSpecialDamage?: Partial<Record<SpecialDamageType, number>>;
  newCrits:            number;
}

const SHARPNESS_LIMBS = ['left arm', 'right arm', 'left leg', 'right leg'];

function rollSharpnessLimb(): string {
  return SHARPNESS_LIMBS[Math.floor(Math.random() * SHARPNESS_LIMBS.length)];
}

function sharpnessEligibility(naturalRoll: number): string {
  if (naturalRoll >= 20) return 'can sever Medium or smaller, Large or larger, and solid metal or stone limbs';
  if (naturalRoll >= 19) return 'can sever Medium or smaller and Large or larger creature limbs';
  return 'can sever limbs of creatures up to Medium size';
}

function resolveWeaponSpecialEffects(atk: AttackRollResult, isHit: boolean): WeaponSpecialEffectResult[] | undefined {
  if (!isHit || !atk.weaponProperties?.length) return undefined;
  const effects: WeaponSpecialEffectResult[] = [];
  if (atk.weaponProperties.includes('vorpal') && atk.d20 === 20) {
    effects.push({ property: 'vorpal', naturalRoll: atk.d20, effect: "target loses its head" });
  }
  if (atk.weaponProperties.includes('sharpness') && atk.d20 >= 18) {
    effects.push({
      property:    'sharpness',
      naturalRoll: atk.d20,
      effect:      'severs a non-head limb',
      limb:        rollSharpnessLimb(),
      eligibility: sharpnessEligibility(atk.d20),
    });
  }
  return effects.length ? effects : undefined;
}

interface Props {
  routine:      AttackRoutine;
  characterId:  string;
  charName?:    string;
  /** Live character record — used to resolve stat references in attack modifiers. */
  char?:        import('../../../../backend/shared/types/index').Character | null;
  modifiers:    CombatModifierWithStates[];
  /**
   * Destinations that receive the same result after execution. An empty array is copy-only.
   * 'dndbeyond' — writes a PendingRoll; plugin routes to D&D Beyond tabs only.
   * 'roll20'    — writes a PendingRoll; plugin routes to Roll20 tabs only.
   * 'talespire' — writes a PendingRoll; the TaleSpire Symbiote publishes it board-wide.
   * 'discord'   — posts server-side to the campaign combat channel.
   */
  outputTargets?: Array<'dndbeyond' | 'roll20' | 'talespire' | 'discord'>;
  onClose:      () => void;
}

export default function RoutineExecutor({ routine, characterId, charName, char, modifiers, outputTargets = [], onClose }: Props) {
  // Active Darkfeld theme — included in every Roll20 dispatch payload so the
  // chat card palette matches what the player sees on the site.
  const themeId = useAppSelector((s) => s.theme.themeId);

  const [phase,     setPhase]     = useState<Phase>('idle');
  const [pending,   setPending]   = useState<PendingRollResult | null>(null);
  const [hitMask,   setHitMask]   = useState<boolean[]>([]);
  const [completed, setCompleted] = useState<CompletedRollResult | null>(null);
  const [exporting,       setExporting]       = useState(false);
  const [copied,          setCopied]          = useState<'discord' | 'plain' | null>(null);
  const [sizeSteps,       setSizeSteps]       = useState(0);
  const [inAntimagic,     setInAntimagic]     = useState(false);
  // null = derived from active modifiers; explicit value = manual override
  const [rollModeOverride, setRollModeOverride] = useState<RollMode | null>(null);

  // Primary result (stored while bonus rounds are in progress)
  const [primaryResult,   setPrimaryResult]   = useState<CompletedRollResult | null>(null);
  // Bonus round state
  const [bonusRoundNum,   setBonusRoundNum]   = useState(0);
  const [bonusRoundEntries, setBonusRoundEntries] = useState<BonusRoundEntry[]>([]);
  const [bonusHitMask,    setBonusHitMask]    = useState<boolean[]>([]);
  const [bonusRoundHistory, setBonusRoundHistory] = useState<ResolvedBonusRound[]>([]);

  // Phase 1 / Phase 3 dispatch gating:
  // After announce-rolls we store the rollId and poll until the plugin ACKs it.
  // The "Confirm Hits" button is disabled until ACKed (or the 15s safety timeout fires),
  // ensuring Phase 3 (damage_results) is never written before the plugin has processed Phase 1.
  const [phase1RollIds,   setPhase1RollIds]   = useState<string[]>([]);
  const [phase1Acked,     setPhase1Acked]     = useState(false);
  const phase1PollRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const phase1TimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Poll every 2s until every target-specific Phase 1 record is acknowledged.
  // Timeout after 15s to not block users when the plugin isn't running.
  useEffect(() => {
    if (phase1RollIds.length === 0) {
      setPhase1Acked(true); // no VTT tab dispatch — never block Confirm
      return;
    }
    setPhase1Acked(false);
    let cancelled = false;

    const poll = async () => {
      if (cancelled) return;
      try {
        const { pending: rolls } = await apiFetch<{ pending: Array<{ rollId: string }> }>(
          '/users/me/pending-rolls',
        );
        if (!rolls.some((r) => phase1RollIds.includes(r.rollId))) {
          if (!cancelled) setPhase1Acked(true);
          return; // done — stop polling
        }
      } catch { /* ignore — poll again next cycle */ }
      if (!cancelled) phase1PollRef.current = setTimeout(poll, 2000);
    };

    // First poll after 1.5 s (give the plugin time to process)
    phase1PollRef.current = setTimeout(poll, 1500);

    // Safety timeout — unblock Confirm after 15 s regardless of ACK
    phase1TimeoutRef.current = setTimeout(() => {
      if (!cancelled) setPhase1Acked(true);
    }, 15_000);

    return () => {
      cancelled = true;
      if (phase1PollRef.current)    clearTimeout(phase1PollRef.current);
      if (phase1TimeoutRef.current) clearTimeout(phase1TimeoutRef.current);
    };
  }, [phase1RollIds]);

  // Active modifier IDs — shown as opt-out toggles
  const active = useMemo(() => activeModifiersForCharacter(modifiers, characterId), [modifiers, characterId]);
  const [disabledMods, setDisabledMods] = useState<Set<string>>(new Set());
  const is5eRoutine = routine.gameSystem === 'dnd5e';

  const suppressedByAntimagic = (m: CombatModifierWithStates) => inAntimagic && m.worksInAntimagic !== true;
  const enabledMods    = active.filter((m) => !disabledMods.has(m.modifierId) && !suppressedByAntimagic(m));
  const enabledModIds  = enabledMods.map((m) => m.modifierId);

  function attackTargetAttrs(attack: AttackNode): Set<string> {
    return combatModifierTargets('attack', attack.attackKind);
  }

  function damageTargetAttrs(attack: AttackNode): Set<string> {
    return combatModifierTargets('damage', attack.attackKind);
  }

  const attackBonusFor = (attack: AttackNode) =>
    resolveFlat(enabledMods as CombatModifierWithStates[], characterId, attackTargetAttrs(attack));
  const damageBonusFor = (attack: AttackNode) =>
    resolveFlat(enabledMods as CombatModifierWithStates[], characterId, damageTargetAttrs(attack));

  // Derive roll mode from active advantage/disadvantage modifier effects on attack rolls
  const rollModeFor = (attack: AttackNode): RollMode => {
    const targetAttrs = attackTargetAttrs(attack);
    let hasAdv = false, hasDis = false;
    for (const mod of enabledMods) {
      for (const e of mod.effects) {
        if (!e.rollModifier) continue;
        if (!targetAttrs.has(e.targetAttribute)) continue;
        if (e.rollModifier === 'advantage')    hasAdv = true;
        if (e.rollModifier === 'disadvantage') hasDis = true;
      }
    }
    if (hasAdv && hasDis) return 'normal';
    if (hasAdv)           return 'advantage';
    if (hasDis)           return 'disadvantage';
    return 'normal';
  };

  const derivedRollMode = (() : RollMode => {
    const modes = routine.definition
      .flatMap((node) => node.type === 'attack'
        ? [rollModeFor(node)]
        : node.type === 'bonus_attack'
          ? [rollModeFor(node.attack)]
          : []);
    if (modes.includes('advantage') && modes.includes('disadvantage')) return 'normal';
    if (modes.includes('advantage')) return 'advantage';
    if (modes.includes('disadvantage')) return 'disadvantage';
    return 'normal';
  })();

  // Final rollMode: manual override wins if set; otherwise derived
  const rollMode = rollModeOverride ?? rollModeFor;
  const displayRollMode = rollModeOverride ?? derivedRollMode;
  const modifierCalculations = buildModifierCalculations(routine, enabledMods, char);

  // Escape key — close the executor (but not while typing in an input/textarea)
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      const target = e.target as HTMLElement;
      if (target?.tagName === 'INPUT' || target?.tagName === 'TEXTAREA') return;
      e.preventDefault();
      onClose();
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [onClose]);

  // Collect all size-tagged damage rows across all attack nodes
  const sizeDamageRows = useMemo(() => {
    const rows: { attackLabel: string; diceExpr: string; weaponSize: WeaponSize }[] = [];
    for (const node of routine.definition) {
      const atk = node.type === 'attack' ? node
        : node.type === 'bonus_attack' ? node.attack
        : null;
      if (!atk) continue;
      for (const d of [...atk.damage, ...(atk.onCritExtra ?? [])]) {
        if (d.weaponSize && d.diceExpr) {
          rows.push({ attackLabel: atk.label, diceExpr: d.diceExpr, weaponSize: d.weaponSize });
        }
      }
    }
    return rows;
  }, [routine]);

  // Phase A — Roll, then immediately send attack rolls (to-hit only) to plugin
  async function handleRoll() {
    const result = rollPhaseA(
      routine, attackBonusFor, damageBonusFor, enabledModIds, char, rollMode, sizeSteps,
      enabledMods.map((m) => m.name),
      charName,
    );
    setPending(result);
    setHitMask(result.attackResults.map((atk) => atk.d20 !== 1));  // nat 1 defaults to miss
    setPhase1RollIds([]);   // reset ACK state before starting a new roll
    setPhase1Acked(false);
    setPhase('rolled');

    // Phase 1 dispatch: announce-rolls stores to-hit data only (backend strips damage).
    // We capture the returned rollId and poll /users/me/pending-rolls until the plugin
    // ACKs it. "Confirm Hits" stays disabled until ACKed or the 15s safety timeout fires,
    // guaranteeing Phase 1 and Phase 3 (damage) arrive as separate plugin cards.
    if (outputTargets.length > 0) {
      try {
        const { rollIds = [] } = await apiFetch<{ rollIds?: string[] }>(
          `/characters/${characterId}/combat/routines/${routine.routineId}/announce-rolls`,
          { method: 'POST', body: JSON.stringify({ ...result, outputTargets, r20Theme: themeId }) },
        );
        setPhase1RollIds(rollIds);  // triggers the ACK-polling useEffect
        if (rollIds.length === 0) setPhase1Acked(true);
      } catch {
        setPhase1Acked(true); // announce failed — unblock Confirm so play can continue
      }
    } else {
      setPhase1Acked(true); // plugin disabled — never block
    }
  }

  // Helper: collect crit_hits BonusAttackNodes from the routine definition
  const critHitsBonusNodes = useMemo(
    () => routine.definition.filter(
      (n): n is BonusAttackNode => n.type === 'bonus_attack' && n.condition.subject === 'crit_hits',
    ),
    [routine],
  );

  // Helper: roll and enter a bonus round
  function enterBonusRound(critCount: number, roundNum: number) {
    const entries: BonusRoundEntry[] = [];
    for (const node of critHitsBonusNodes) {
      for (let i = 0; i < critCount; i++) {
        entries.push({
          nodeId:    node.id,
          nodeLabel: node.label ?? node.attack.label,
          atk:       rollOneBonusAttack(node.attack, attackBonusFor, damageBonusFor, char, routine.gameSystem, rollModeOverride ?? rollModeFor(node.attack)),
        });
      }
    }

    // Phase A Discord post for this bonus round when Discord is enabled.
    // VTT bonus rolls continue through the primary pending-roll flow.
    // Fire-and-forget — never blocks the bonus hit-selection UI.
    if (outputTargets.includes('discord')) {
      apiFetch(
        `/characters/${characterId}/combat/routines/${routine.routineId}/announce-rolls`,
        {
          method: 'POST',
          body: JSON.stringify({
            routineId:                routine.routineId,
            routineName:              `${routine.name} — Bonus Round ${roundNum}`,
            characterName:            charName,
            gameSystem:               routine.gameSystem,
            rollMode:                 'normal',
            attackResults:            entries.map((e) => e.atk),
            conditionalDamageResults: [],
            appliedModifierIds:       enabledModIds,
            appliedModifierNames:     enabledMods.map((m) => m.name),
            rolledAt:                 Date.now(),
            outputTargets:            ['discord'],
          }),
        },
      ).catch(() => {}); // non-critical — Discord failure never blocks play
    }

    setBonusRoundEntries(entries);
    setBonusHitMask(entries.map((e) => e.atk.d20 !== 1));  // nat 1 defaults to miss
    setBonusRoundNum(roundNum);
    setPhase('bonus_round');
  }

  // Finalize: merge primary + all bonus rounds into a single CompletedRollResult,
  // then dispatch to VTT and server.
  async function finalizeResult(
    primary: CompletedRollResult,
    history: ResolvedBonusRound[],
  ) {
    // Build bonus attack result entries from confirmed rounds.
    // Include ALL attacks (hits AND misses) so Discord and roll history both
    // show the full picture.  Each attack carries a synthetic `isHit` boolean
    // (not in the AttackRollResult type; read via `(atk as any).isHit` in the
    // Discord embed builder).  Damage is only present for hits since rollPhaseA
    // pre-rolls damage, but rollOneBonusAttack only adds usable damage when hit.
    const bonusResultsFromRounds: typeof primary.bonusAttackResults = [];
    for (const round of history) {
      for (const nodeId of [...new Set(round.entries.map((e) => e.nodeId))]) {
        const allForNode = round.entries.filter((e) => e.nodeId === nodeId);
        if (allForNode.length === 0) continue;
        const existing = bonusResultsFromRounds.find((b) => b.nodeId === nodeId);
        if (existing) {
          existing.attackResults.push(...allForNode.map((e) => ({
            ...e.atk,
            isHit: e.isHit,
            specialEffects: (e.atk as any).specialEffects,
          } as any)));
        } else {
          const node = critHitsBonusNodes.find((n) => n.id === nodeId);
          bonusResultsFromRounds.push({
            nodeId,
            label:          allForNode[0].nodeLabel,
            condition:      node?.condition ?? { subject: 'crit_hits', op: 'gte', value: 1 },
            triggered:      true,
            attackResults:  allForNode.map((e) => ({
              ...e.atk,
              isHit: e.isHit,
              specialEffects: (e.atk as any).specialEffects,
            } as any)),
          });
        }
      }
    }

    const bonusDamage = history.reduce((s, r) => s + r.roundDamage, 0);

    // Merge special damage from bonus rounds into the primary specialDamageTotals.
    const mergedSpecial = { ...(primary.specialDamageTotals ?? {}) };
    for (const round of history) {
      for (const [type, amt] of Object.entries(round.roundSpecialDamage ?? {})) {
        const t = type as SpecialDamageType;
        mergedSpecial[t] = (mergedSpecial[t] ?? 0) + (amt ?? 0);
      }
    }
    const hasSpecial = Object.keys(mergedSpecial).length > 0;

    const finalResult: CompletedRollResult = {
      ...primary,
      bonusAttackResults: [
        ...(primary.bonusAttackResults?.filter((b) => b.triggered) ?? []),
        ...bonusResultsFromRounds,
      ],
      totalDamage:       primary.totalDamage + bonusDamage,
      specialDamageTotals: hasSpecial ? mergedSpecial : undefined,
    };

    setCompleted(finalResult);
    setPhase('resolved');

    try {
      await apiFetch(
        `/characters/${characterId}/combat/routines/${routine.routineId}/execute`,
        { method: 'POST', body: JSON.stringify({ ...finalResult, outputTargets, r20Theme: themeId }) },
      );
    } catch { /* non-critical */ }
  }

  // Phase C — Resolve primary hits
  async function handleConfirm() {
    if (!pending) return;
    const result = rollPhaseC(pending, hitMask, charName, char);

    const primaryCrits = result.resolvedAttacks.filter((a) => a.isHit && a.isCrit).length;

    if (primaryCrits > 0 && critHitsBonusNodes.length > 0) {
      setPrimaryResult(result);
      setBonusRoundHistory([]);
      enterBonusRound(primaryCrits, 1);
    } else {
      setCompleted(result);
      setPhase('resolved');
      try {
        await apiFetch(
          `/characters/${characterId}/combat/routines/${routine.routineId}/execute`,
          { method: 'POST', body: JSON.stringify({ ...result, outputTargets, r20Theme: themeId }) },
        );
      } catch { /* non-critical */ }
    }
  }

  // Bonus round confirmation — resolve hits, check for more crits
  function handleConfirmBonusRound() {
    const is5e = (primaryResult ?? completed)?.gameSystem === 'dnd5e';

    let roundDamage = 0;
    let newCrits    = 0;
    const roundSpecialDamage: Partial<Record<SpecialDamageType, number>> = {};

    const resolvedEntries: Array<BonusRoundEntry & { isHit: boolean }> = bonusRoundEntries.map((e, i) => {
      const isHit = bonusHitMask[i] ?? false;
      const specialEffects = resolveWeaponSpecialEffects(e.atk, isHit);
      if (isHit) {
        if (e.atk.isCrit) newCrits++;
        for (const d of e.atk.damage) {
          const mult = d.critMult;
          let rowAmt: number;
          if (!e.atk.isCrit || mult <= 1) {
            rowAmt = d.total;
          } else if (is5e) {
            if (e.atk.critStyle === 'perfect') {
              const parsed = /^(\d+)d(\d+)$/i.exec(d.diceExpr.trim());
              const diceSum = d.rolls.reduce((s: number, r: number) => s + r, 0);
              const maxDice = parsed ? Number(parsed[1]) * Number(parsed[2]) : diceSum;
              rowAmt = maxDice + diceSum + d.modifier;
            } else {
              rowAmt = d.rolls.reduce((s: number, r: number) => s + r, 0) * 2 + d.modifier;
            }
          } else {
            rowAmt = d.total * mult;
          }
          if (d.specialType) {
            roundSpecialDamage[d.specialType] = (roundSpecialDamage[d.specialType] ?? 0) + rowAmt;
          } else {
            roundDamage += rowAmt;
          }
        }
        if (e.atk.isCrit && e.atk.damage.length > 0) {
          for (const d of e.atk.onCritExtraDamage) {
            if (d.specialType) {
              roundSpecialDamage[d.specialType] = (roundSpecialDamage[d.specialType] ?? 0) + d.total;
            } else {
              roundDamage += d.total;
            }
          }
        }
      }
      return { ...e, atk: { ...e.atk, specialEffects }, isHit };
    });

    const round: ResolvedBonusRound = {
      roundNum:    bonusRoundNum,
      entries:     resolvedEntries,
      roundDamage,
      roundSpecialDamage: Object.keys(roundSpecialDamage).length > 0 ? roundSpecialDamage : undefined,
      newCrits,
    };

    const updatedHistory = [...bonusRoundHistory, round];
    setBonusRoundHistory(updatedHistory);

    if (newCrits > 0) {
      enterBonusRound(newCrits, bonusRoundNum + 1);
    } else {
      finalizeResult(primaryResult!, updatedHistory);
    }
  }

  // Manual clipboard export (Discord or plain text) — fallback for players
  // without the plugin, or when they want to copy manually.
  // IMPORTANT: always passes outputTarget:'none' so the execute endpoint does NOT
  // write a second pending-roll record to DynamoDB. The plugin has already received
  // the damage_results pending roll from handleConfirm(); creating another here
  // would cause the plugin to display the result a second time.
  async function handleExport(format: 'discord' | 'plain') {
    if (!completed) return;
    setExporting(true);
    try {
      const res = await apiFetch<{ discord: string; plainText: string }>(
        `/characters/${characterId}/combat/routines/${routine.routineId}/execute`,
        { method: 'POST', body: JSON.stringify({ ...completed, outputTarget: 'none', r20Theme: themeId }) },
      );
      const text = format === 'discord' ? res.discord : res.plainText;
      await navigator.clipboard.writeText(text);
      setCopied(format);
      setTimeout(() => setCopied(null), 2000);
    } catch {
      // Fall back to client-side formatting if execute endpoint fails
      const text = format === 'discord'
        ? formatForDiscord(completed)
        : formatForClipboard(completed);
      await navigator.clipboard.writeText(text);
      setCopied(format);
      setTimeout(() => setCopied(null), 2000);
    } finally {
      setExporting(false);
    }
  }

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center gap-2">
        <Swords className="w-4 h-4 text-gold/70 shrink-0" />
        <h3 className="font-display text-sm text-gold flex-1 truncate">{routine.name}</h3>
        <button onClick={onClose} className="p-1 text-parchment-muted/40 hover:text-parchment transition-colors">
          <X className="w-4 h-4" />
        </button>
      </div>

      {/* ── Idle: modifier opt-out + Roll button ── */}
      {phase === 'idle' && (
        <div className="space-y-3">
          {active.length > 0 && (
            <div className="space-y-1">
              <div className="space-y-0.5 text-[10px] text-parchment-muted/55">
                <p className="uppercase tracking-widest text-parchment-muted/40">Applying modifiers</p>
                {modifierCalculations.map((line, index) => (
                  <p key={`${line.label}-${index}`} className="normal-case tracking-normal leading-relaxed">
                    <span className="text-parchment-muted/70">{line.label}:</span>{' '}
                    {line.terms.length > 0 ? line.terms.map(formatCalculationTerm).join(', ') : '+0 base'}
                    {' = '}
                    <span className="text-gold/70">{line.total >= 0 ? '+' : ''}{line.total}</span>
                  </p>
                ))}
              </div>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setInAntimagic((v) => !v)}
                  className="shrink-0"
                  title={inAntimagic ? 'Disable Antimagic override' : 'Enable Antimagic override'}
                >
                  {inAntimagic
                    ? <ToggleRight className="w-4 h-4 text-danger" />
                    : <ToggleLeft  className="w-4 h-4 text-parchment-muted/30" />}
                </button>
                <span className={`text-xs truncate ${inAntimagic ? 'text-danger/70' : 'text-parchment-muted/50'}`}>
                  In Antimagic?
                </span>
              </div>
              {active.map((m) => {
                const off = disabledMods.has(m.modifierId);
                const antimagicOff = suppressedByAntimagic(m);
                const disabled = off || antimagicOff;
                return (
                  <div key={m.modifierId} className="flex items-center gap-2">
                    <button
                      onClick={() => {
                        if (antimagicOff) return;
                        setDisabledMods((prev) => {
                          const next = new Set(prev);
                          off ? next.delete(m.modifierId) : next.add(m.modifierId);
                          return next;
                        });
                      }}
                      className="shrink-0"
                      title={antimagicOff ? 'Disabled by Antimagic' : off ? 'Enable for this roll' : 'Disable for this roll'}
                    >
                      {disabled
                        ? <ToggleLeft  className="w-4 h-4 text-parchment-muted/30" />
                        : <ToggleRight className="w-4 h-4 text-success" />}
                    </button>
                    <span className={`text-xs truncate ${disabled ? 'line-through text-parchment-muted/30' : 'text-parchment/70'}`}>
                      {m.name}
                      {antimagicOff && <span className="ml-1 text-danger/60 no-underline">(Antimagic)</span>}
                    </span>
                  </div>
                );
              })}
            </div>
          )}
          {/* Advantage / disadvantage toggle — shown for 5e always,
              or for any system when a modifier grants adv/dis on attacks */}
          {(is5eRoutine || derivedRollMode !== 'normal') && (
            <div className="space-y-1">
              {derivedRollMode !== 'normal' && rollModeOverride === null && (
                <p className="text-[10px] text-parchment-muted/40">
                  Roll mode set by active modifier — override below if needed
                </p>
              )}
              <div className="flex items-center gap-1 bg-darkbg-tertiary rounded-lg p-1">
                {(['normal', 'advantage', 'disadvantage'] as RollMode[]).map((mode) => (
                  <button
                    key={mode}
                    onClick={() => setRollModeOverride(rollModeOverride === mode ? null : mode)}
                    className={`flex-1 py-1.5 text-xs rounded transition-colors capitalize ${
                      displayRollMode === mode
                        ? mode === 'advantage' ? 'bg-success text-white font-semibold'
                          : mode === 'disadvantage' ? 'bg-danger text-white font-semibold'
                          : 'bg-darkbg-card text-parchment font-semibold'
                        : 'text-parchment-muted/50 hover:text-parchment transition-colors'
                    }`}
                  >
                    {mode}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Size adjustment — only shown for routines with size-tagged damage rows */}
          {sizeDamageRows.length > 0 && (
            <div className="bg-darkbg-tertiary rounded-lg p-2.5 space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-[10px] uppercase tracking-widest text-parchment-muted/40">
                  Damage Dice Size
                </span>
                {sizeSteps !== 0 && (
                  <button onClick={() => setSizeSteps(0)}
                    className="text-[10px] text-parchment-muted/40 hover:text-gold transition-colors">
                    Reset
                  </button>
                )}
              </div>
              <div className="flex items-center gap-2">
                <button onClick={() => setSizeSteps((s) => s - 1)}
                  className="w-7 h-7 rounded bg-darkbg border border-darkbg-border text-parchment-muted/60
                             hover:border-gold/40 hover:text-parchment transition-colors text-sm font-bold">
                  −
                </button>
                <span className={`flex-1 text-center text-xs font-display ${sizeSteps === 0 ? 'text-parchment-muted/40' : sizeSteps > 0 ? 'text-success' : 'text-danger'}`}>
                  {sizeStepLabel(sizeSteps)}
                </span>
                <button onClick={() => setSizeSteps((s) => s + 1)}
                  className="w-7 h-7 rounded bg-darkbg border border-darkbg-border text-parchment-muted/60
                             hover:border-gold/40 hover:text-parchment transition-colors text-sm font-bold">
                  +
                </button>
              </div>
              {/* Preview adjusted dice for each size-tagged row */}
              {sizeSteps !== 0 && (
                <div className="space-y-0.5">
                  {sizeDamageRows.map((row, idx) => {
                    const adjusted = adjustDiceForSizeChange(row.diceExpr, row.weaponSize, sizeSteps);
                    const sizeLabel = WEAPON_SIZES.find((s) => s.id === row.weaponSize)?.label ?? row.weaponSize;
                    return (
                      <p key={idx} className="text-[10px] font-mono text-parchment-muted/50">
                        <span className="text-parchment-muted/30">{row.attackLabel} · </span>
                        <span className="text-parchment/60">{row.diceExpr}</span>
                        <span className="text-parchment-muted/30"> ({sizeLabel}) → </span>
                        <span className={adjusted !== row.diceExpr ? 'text-gold/80' : 'text-parchment-muted/40'}>{adjusted}</span>
                      </p>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          <button onClick={handleRoll}
            title={`Roll ${routine.name} — attacks and damage`}
            className="w-full btn-primary flex items-center justify-center gap-3 py-3
                       hover:brightness-110 transition-all">
            {/* D20 icon — 20-sided die */}
            <svg viewBox="0 0 24 24" className="w-6 h-6" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round">
              <polygon points="12,2 22,7 22,17 12,22 2,17 2,7" />
              <text x="12" y="15.5" textAnchor="middle" fontSize="7" fontWeight="bold"
                fill="currentColor" stroke="none" fontFamily="serif">20</text>
            </svg>
            <span className="font-display text-sm tracking-wide">Roll Attacks</span>
          </button>
        </div>
      )}

      {/* ── Rolled: attack results + hit toggles ── */}
      {phase === 'rolled' && pending && (
        <div className="space-y-3">
          <div className="space-y-2">
            {pending.attackResults.map((atk, i) => {
              const isHit = hitMask[i] ?? true;
              return (
                <div key={atk.nodeId}
                  className={`flex items-center gap-2 sm:gap-3 px-2 sm:px-3 py-2.5 rounded-lg border transition-colors ${
                    isHit ? 'bg-darkbg-card border-darkbg-border' : 'bg-darkbg border-darkbg-border/40 opacity-60'
                  }`}>
                  {/* Hit toggle — larger on mobile for touch-friendly tapping */}
                  <button onClick={() => setHitMask((prev) => prev.map((v, j) => j === i ? !v : v))}
                    className="shrink-0 p-1 sm:p-0 rounded sm:rounded-none hover:bg-darkbg-tertiary/30 sm:hover:bg-transparent transition-colors"
                    title={isHit ? 'Click to mark as miss' : 'Click to mark as hit'}>
                    {isHit
                      ? <ToggleRight className="w-6 h-6 sm:w-5 sm:h-5 text-success" />
                      : <ToggleLeft  className="w-6 h-6 sm:w-5 sm:h-5 text-parchment-muted/30" />}
                  </button>

                  {/* Label + roll */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-display text-xs sm:text-sm text-parchment truncate">{atk.label}</span>
                      {atk.isCrit && isHit && (
                        <span className="text-[10px] text-gold/80 bg-gold/10 border border-gold/20 px-1.5 py-0.5 rounded shrink-0">
                          Critical Hit
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-parchment-muted/50">
                      {atk.advantageDie !== undefined && (
                        <span className="font-mono tabular-nums font-bold text-parchment-muted/30 mr-1">
                          [{atk.rollMode === 'advantage'
                            ? Math.min(atk.d20, atk.advantageDie)
                            : Math.max(atk.d20, atk.advantageDie)}]
                        </span>
                      )}
                      <span className={`font-mono tabular-nums font-bold ${atk.d20 === 20 ? 'text-gold' : atk.d20 === 1 ? 'text-danger' : ''}`}>
                        [{atk.d20}]
                      </span>
                      {atk.advantageDie !== undefined && (
                        <span className="text-[10px] text-parchment-muted/40 ml-1">
                          {atk.rollMode === 'advantage' ? '(adv)' : '(dis)'}
                        </span>
                      )}
                      {' '}+{atk.attackMod} = <span className="text-parchment font-semibold">{atk.total}</span>
                      {' '}vs {atk.vsTarget}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Plugin Phase 1 ACK status — shown only when plugin is active */}
          {outputTargets.length > 0 && (
            <div className="flex items-center gap-1.5 text-[10px] text-parchment-muted/40 px-1">
              {phase1Acked
                ? <><CheckCircle2 className="w-3 h-3 text-success/60 shrink-0" /> Attack rolls sent to table — select hits then confirm</>
                : <><Loader2 className="w-3 h-3 animate-spin shrink-0" /> Sending attack rolls to table…</>}
            </div>
          )}

          <div className="flex items-center gap-2">
            <button onClick={() => { setPhase('idle'); setPending(null); setHitMask([]); setPhase1RollIds([]); setPhase1Acked(false); }}
              className="flex-1 py-1.5 text-xs text-parchment-muted/60 border border-darkbg-border rounded hover:text-parchment transition-colors">
              Re-roll
            </button>
            <button
              onClick={handleConfirm}
              disabled={!phase1Acked}
              className="flex-1 btn-primary py-1.5 text-xs disabled:opacity-50 disabled:cursor-wait"
              title={!phase1Acked ? 'Waiting for plugin to receive Phase 1…' : undefined}
            >
              {phase1Acked ? 'Confirm Hits →' : <><Loader2 className="w-3 h-3 animate-spin inline mr-1" />Waiting…</>}
            </button>
          </div>
        </div>
      )}

      {/* ── Bonus Round: confirm bonus attack hits ── */}
      {phase === 'bonus_round' && (
        <div className="space-y-3">
          {/* Round header */}
          <div className="flex items-center gap-2 px-3 py-2 bg-gold/10 border border-gold/30 rounded-lg">
            <span className="text-[10px] uppercase tracking-widest text-gold/70">Bonus Round {bonusRoundNum}</span>
            <span className="text-[10px] text-parchment-muted/40 ml-1">
              — {bonusRoundEntries.length} attack{bonusRoundEntries.length !== 1 ? 's' : ''} triggered by critical {bonusRoundNum === 1 ? 'hits' : `hits from Round ${bonusRoundNum - 1}`}
            </span>
          </div>

          {/* Attack rolls — same hit-toggle pattern as primary */}
          <div className="space-y-2">
            {bonusRoundEntries.map((entry, i) => {
              const isHit = bonusHitMask[i] ?? true;
              const atk = entry.atk;
              return (
                <div key={`${entry.nodeId}-${i}`}
                  className={`flex items-center gap-2 sm:gap-3 px-2 sm:px-3 py-2.5 rounded-lg border transition-colors ${
                    isHit ? 'bg-darkbg-card border-gold/20' : 'bg-darkbg border-darkbg-border/40 opacity-60'
                  }`}>
                  <button
                    onClick={() => setBonusHitMask((prev) => prev.map((v, j) => j === i ? !v : v))}
                    className="shrink-0 p-1 sm:p-0 rounded hover:bg-darkbg-tertiary/30 transition-colors"
                    title={isHit ? 'Mark as miss' : 'Mark as hit'}>
                    {isHit
                      ? <ToggleRight className="w-6 h-6 sm:w-5 sm:h-5 text-success" />
                      : <ToggleLeft  className="w-6 h-6 sm:w-5 sm:h-5 text-parchment-muted/30" />}
                  </button>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-display text-xs sm:text-sm text-parchment truncate">{entry.nodeLabel}</span>
                      {atk.isCrit && isHit && (
                        <span className="text-[10px] text-gold/80 bg-gold/10 border border-gold/20 px-1.5 py-0.5 rounded shrink-0">
                          Critical Hit ⚡
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-parchment-muted/50 font-mono tabular-nums">
                      <span className={`font-bold ${atk.d20 === 20 ? 'text-gold' : atk.d20 === 1 ? 'text-danger' : ''}`}>
                        [{atk.d20}]
                      </span>
                      {' '}+{atk.attackMod} = <span className="text-parchment font-semibold">{atk.total}</span>
                      {' '}vs {atk.vsTarget}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Actions */}
          <div className="flex items-center gap-2">
            <button
              onClick={() => setBonusHitMask(bonusRoundEntries.map((e) => e.atk.d20 !== 1))}
              className="px-3 py-1.5 text-xs text-parchment-muted/60 border border-darkbg-border rounded hover:text-parchment transition-colors">
              Reset
            </button>
            <button onClick={handleConfirmBonusRound}
              className="flex-1 btn-primary py-1.5 text-xs">
              Confirm Bonus Hits →
            </button>
          </div>
        </div>
      )}

      {/* ── Resolved: summary + export ── */}
      {phase === 'resolved' && completed && (
        <div className="space-y-3">
          {/* Per-attack breakdown */}
          <div className="space-y-2">
            {completed.resolvedAttacks.map((atk) => {
              const isCrit = atk.isCrit && atk.isHit;
              const is5e   = completed.gameSystem === 'dnd5e';
              const modStr = `${atk.attackMod >= 0 ? '+' : ''}${atk.attackMod}`;

              return (
                <div key={atk.nodeId}
                  className={`rounded-lg border px-3 py-2 space-y-1.5 ${
                    atk.isHit ? 'bg-darkbg-card border-darkbg-border' : 'bg-darkbg border-darkbg-border/30 opacity-50'
                  }`}>
                  {/* Attack roll line */}
                  <div className="flex items-center gap-2 text-xs">
                    <span className={isCrit ? 'text-gold' : atk.isHit ? 'text-success' : 'text-parchment-muted/40'}>
                      {isCrit ? '⚡' : atk.isHit ? '✅' : '❌'}
                    </span>
                    <span className={`font-display flex-1 min-w-0 truncate ${atk.isHit ? 'text-parchment' : 'text-parchment-muted/40'}`}>
                      {atk.label}
                      {isCrit && (
                        <span className="ml-1.5 text-[10px] text-gold/70 bg-gold/10 border border-gold/20 px-1.5 py-0.5 rounded">
                          {is5e ? (atk.critStyle === 'perfect' ? 'Perfect Crit' : 'Crit (dice ×2)') : 'Critical Hit'}
                        </span>
                      )}
                    </span>
                    <span className="font-mono text-parchment-muted/50 tabular-nums shrink-0">
                      d20 [<span className={atk.d20 === 20 ? 'text-gold font-bold' : atk.d20 === 1 ? 'text-danger' : ''}>{atk.d20}</span>]
                      {' '}{modStr} = <span className="text-parchment font-semibold">{atk.total}</span>
                    </span>
                  </div>

                  {/* Damage breakdown — only when the attack has damage rows */}
                  {atk.isHit && atk.damage.length === 0 && (
                    <p className="text-[11px] text-parchment-muted/40 italic pl-3 pt-0.5">
                      No damage roll — check result only
                    </p>
                  )}
                  {atk.isHit && atk.damage.length > 0 && (
                    <div className="pl-5 space-y-0.5">
                      {atk.damage.map((d) => {
                        const hasDice   = d.rolls.length > 0;
                        const diceSum   = d.rolls.reduce((s, r) => s + r, 0);
                        const rollsStr  = hasDice ? `[${d.rolls.join(', ')}]` : '';
                        const modPart   = d.modifier !== 0 ? ` ${d.modifier > 0 ? '+' : ''}${d.modifier}` : '';
                        const isSpecial = !!d.specialType;
                        // For special rows: show the structured label; for HP rows show notes.
                        const typeLabel = isSpecial
                          ? `  ${SPECIAL_DAMAGE_LABELS[d.specialType!]}`
                          : (d.notes ? `  ${d.notes}` : '');
                        const doMult   = isCrit && d.critMult > 1 && !isSpecial;
                        const notMult  = isCrit && d.critMult <= 1 && !isSpecial;

                        let display: string;
                        let rowTotal: number;
                        const exprBase = hasDice ? d.diceExpr.replace(/[+-].*/, '') : '';

                        if (!doMult) {
                          rowTotal = d.total;
                          display  = hasDice
                            ? `${exprBase} ${rollsStr}${modPart} = ${rowTotal}`
                            : `${rowTotal}`;
                        } else if (is5e && atk.critStyle === 'perfect') {
                          const parsed = /^(\d+)d(\d+)$/i.exec(d.diceExpr.trim());
                          const maxDice = parsed ? Number(parsed[1]) * Number(parsed[2]) : diceSum;
                          rowTotal = maxDice + diceSum + d.modifier;
                          display  = `${exprBase} ${rollsStr}${modPart} = max ${maxDice} + crit ${diceSum} = ${rowTotal}`;
                        } else if (is5e) {
                          rowTotal = diceSum * 2 + d.modifier;
                          display  = `${exprBase} ${rollsStr}${modPart} = ${d.total} (dice ×2 +${diceSum}) = ${rowTotal}`;
                        } else {
                          rowTotal = d.total * d.critMult;
                          display  = `${exprBase} ${rollsStr}${modPart} = ${d.total} ×${d.critMult} = ${rowTotal}`;
                        }

                        return (
                          <p key={d.nodeId}
                            className={`text-[11px] font-mono ${
                              isSpecial
                                ? 'text-amber-400/80'
                                : notMult
                                  ? 'text-parchment-muted/40'
                                  : 'text-parchment-muted/70'
                            }`}>
                            {display}
                            <span className={isSpecial ? 'text-amber-400/60' : 'text-parchment-muted/40'}>
                              {typeLabel}{!isSpecial && notMult ? ' (×1)' : ''}
                            </span>
                          </p>
                        );
                      })}
                      {/* On-crit-extra rows (crit only, never multiplied) */}
                      {isCrit && atk.damage.length > 0 && atk.onCritExtraDamage.map((d) => {
                        const hasDice   = d.rolls.length > 0;
                        const rollsStr  = hasDice ? `[${d.rolls.join(', ')}]` : '';
                        const modPart   = d.modifier !== 0 ? ` ${d.modifier > 0 ? '+' : ''}${d.modifier}` : '';
                        const isSpecial = !!d.specialType;
                        const typeLabel = isSpecial
                          ? `  ${SPECIAL_DAMAGE_LABELS[d.specialType!]} (crit bonus)`
                          : (d.notes ? `  ${d.notes} (crit bonus)` : ' (crit bonus)');
                        const exprBase  = hasDice ? d.diceExpr.replace(/[+\-].*/, '') : '';
                        return (
                          <p key={d.nodeId}
                            className={`text-[11px] font-mono ${isSpecial ? 'text-amber-400/70' : 'text-gold/60'}`}>
                            {hasDice ? `${exprBase} ${rollsStr}` : ''}{modPart} = {d.total}
                            <span className={isSpecial ? 'text-amber-400/50' : 'text-parchment-muted/40'}>
                              {typeLabel}
                            </span>
                          </p>
                        );
                      })}
                      {/* Per-attack HP subtotal — label clarifies when special damage is also present */}
                      {(isCrit || atk.damage.length > 1) && (
                        <p className="text-xs font-bold text-parchment tabular-nums pt-0.5">
                          {atk.attackDamage} {atk.damage.some((d) => d.specialType) ? 'HP ' : ''}dmg
                        </p>
                      )}
                    </div>
                  )}
                  {atk.isHit && (atk.specialEffects?.length ?? 0) > 0 && (
                    <div className="pl-5 space-y-0.5">
                      {atk.specialEffects!.map((effect, idx) => (
                        <p key={`${effect.property}-${idx}`} className="text-[11px] text-gold/80">
                          <span className="font-semibold capitalize">{effect.property}</span>
                          {effect.property === 'vorpal'
                            ? `: ${effect.effect}.`
                            : `: ${effect.limb ?? 'limb'} severed. ${effect.eligibility ?? ''}`}
                        </p>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Conditional bonus damage results */}
          {(completed.conditionalDamageResults ?? []).map((c) => (
            <div key={c.nodeId}
              className={`rounded-lg border px-3 py-2 space-y-1 ${
                c.triggered
                  ? 'bg-darkbg-card border-gold/30'
                  : 'bg-darkbg border-darkbg-border/30 opacity-40'
              }`}>
              <div className="flex items-center gap-2 text-xs">
                <span className={c.triggered ? 'text-gold' : 'text-parchment-muted/30'}>
                  {c.triggered ? '⚡' : '○'}
                </span>
                <span className={`font-display flex-1 ${c.triggered ? 'text-parchment' : 'text-parchment-muted/40'}`}>
                  {c.label || 'Conditional Bonus'}
                </span>
                {c.triggered && (
                  <span className="font-bold text-parchment tabular-nums">{c.total} dmg</span>
                )}
                {!c.triggered && (
                  <span className="text-[10px] text-parchment-muted/30 italic">not triggered</span>
                )}
              </div>
              {c.triggered && c.damage.map((d) => (
                <p key={d.nodeId} className="text-[11px] font-mono text-parchment-muted/60 pl-5">
                  {d.rolls.length > 0
                    ? `${d.diceExpr.replace(/[+-].*/, '')} [${d.rolls.join(', ')}]${d.modifier ? ` ${d.modifier > 0 ? '+' : ''}${d.modifier}` : ''} = ${d.total}`
                    : String(d.total)}
                  {d.notes ? <span className="text-parchment-muted/40">  {d.notes}</span> : null}
                </p>
              ))}
            </div>
          ))}

          {/* Bonus attack results (Phase 5: conditional full attacks like Cleave) */}
          {(completed.bonusAttackResults ?? []).map((ba) => (
            <div key={ba.nodeId}
              className={`rounded-lg border px-3 py-2 space-y-1.5 ${
                ba.triggered
                  ? 'bg-darkbg-card border-gold/30'
                  : 'bg-darkbg border-darkbg-border/30 opacity-40'
              }`}>
              <div className="flex items-center gap-2 text-xs">
                <span className={ba.triggered ? 'text-gold' : 'text-parchment-muted/30'}>
                  {ba.triggered ? '⚡' : '○'}
                </span>
                <span className={`font-display flex-1 ${ba.triggered ? 'text-parchment' : 'text-parchment-muted/40'}`}>
                  {ba.label || 'Bonus Attack'}
                </span>
                {ba.triggered && ba.attackResults.length > 1 && (
                  <span className="text-[10px] text-gold/60 font-mono">×{ba.attackResults.length}</span>
                )}
                {!ba.triggered && (
                  <span className="text-[10px] text-parchment-muted/30 italic">condition not met</span>
                )}
              </div>

              {ba.triggered && ba.attackResults.map((atk) => {
                const isCrit = atk.isCrit;
                const is5e   = completed.gameSystem === 'dnd5e';
                const modStr = `${atk.attackMod >= 0 ? '+' : ''}${atk.attackMod}`;

                return (
                  <div key={atk.nodeId} className="pl-3 space-y-0.5 border-l border-gold/20">
                    {/* Attack roll */}
                    <p className="text-[11px] font-mono text-parchment-muted/70">
                      {atk.label}: d20 [{atk.d20}] {modStr} = {atk.total}
                      {isCrit && <span className="text-gold ml-1">⚡ CRIT</span>}
                    </p>
                    {/* Damage rows */}
                    {atk.damage.map((d) => {
                      const hasDice = d.rolls.length > 0;
                      const diceSum = d.rolls.reduce((s: number, r: number) => s + r, 0);
                      const rollsStr = hasDice ? `[${d.rolls.join(', ')}]` : '';
                      const modPart  = d.modifier !== 0 ? ` ${d.modifier > 0 ? '+' : ''}${d.modifier}` : '';
                      const notesStr = d.notes ? `  ${d.notes}` : '';
                      const doMult   = isCrit && d.critMult > 1;
                      const exprBase = hasDice ? d.diceExpr.replace(/[+-].*/, '') : '';

                      let rowTotal: number;
                      let display: string;

                      if (!doMult) {
                        rowTotal = d.total;
                        display = hasDice
                          ? `${exprBase} ${rollsStr}${modPart} = ${rowTotal}`
                          : `${rowTotal}`;
                      } else if (is5e && atk.critStyle === 'perfect') {
                        const parsed = /^(\d+)d(\d+)$/i.exec(d.diceExpr.trim());
                        const maxDice = parsed ? Number(parsed[1]) * Number(parsed[2]) : diceSum;
                        rowTotal = maxDice + diceSum + d.modifier;
                        display = `${exprBase} ${rollsStr}${modPart} = max ${maxDice} + crit ${diceSum} = ${rowTotal}`;
                      } else if (is5e) {
                        rowTotal = diceSum * 2 + d.modifier;
                        display = `${exprBase} ${rollsStr}${modPart} = ${d.total} (dice ×2) = ${rowTotal}`;
                      } else {
                        rowTotal = d.total * d.critMult;
                        display = `${exprBase} ${rollsStr}${modPart} = ${d.total} ×${d.critMult} = ${rowTotal}`;
                      }

                      return (
                        <p key={d.nodeId} className="text-[10px] font-mono text-parchment-muted/60">
                          {display}
                          <span className="text-parchment-muted/40">{notesStr}</span>
                        </p>
                      );
                    })}
                  </div>
                );
              })}
            </div>
          ))}

          {/* Bonus round history — shown only when interactive bonus rounds were played */}
          {bonusRoundHistory.map((round) => (
            <div key={round.roundNum} className="rounded-lg border border-gold/25 bg-darkbg-card px-3 py-2 space-y-1.5">
              <div className="flex items-center gap-2 text-xs">
                <span className="text-gold">⚡</span>
                <span className="font-display text-parchment flex-1">Bonus Round {round.roundNum}</span>
                <span className="text-[10px] text-parchment-muted/40">
                  {round.entries.filter((e) => e.isHit).length}/{round.entries.length} hit
                  {round.roundDamage > 0 && ` · ${round.roundDamage} dmg`}
                </span>
              </div>
              {round.entries.map((entry, ei) => (
                <div key={ei} className={`pl-3 border-l border-gold/20 space-y-0.5 ${!entry.isHit ? 'opacity-40' : ''}`}>
                  <p className="text-[11px] font-mono text-parchment-muted/70">
                    {entry.nodeLabel}: d20 [{entry.atk.d20}] +{entry.atk.attackMod} = {entry.atk.total}
                    {!entry.isHit && <span className="text-parchment-muted/40 ml-1">MISS</span>}
                    {entry.isHit && entry.atk.isCrit && <span className="text-gold ml-1">⚡ CRIT</span>}
                  </p>
                  {entry.isHit && entry.atk.damage.map((d, di) => {
                    const hasDice = d.rolls.length > 0;
                    const rollStr = hasDice ? `[${d.rolls.join(', ')}]` : '';
                    const modPart = d.modifier !== 0 ? ` ${d.modifier > 0 ? '+' : ''}${d.modifier}` : '';
                    const isCrit  = entry.atk.isCrit;
                    const is5e    = completed.gameSystem === 'dnd5e';
                    const doMult  = isCrit && d.critMult > 1;
                    let total = d.total;
                    if (doMult) {
                      if (is5e && entry.atk.critStyle === 'perfect') {
                        const parsed = /^(\d+)d(\d+)$/i.exec(d.diceExpr.trim());
                        const diceSum = d.rolls.reduce((s: number, r: number) => s + r, 0);
                        const maxDice = parsed ? Number(parsed[1]) * Number(parsed[2]) : diceSum;
                        total = maxDice + diceSum + d.modifier;
                      } else {
                        total = is5e
                          ? d.rolls.reduce((s: number, r: number) => s + r, 0) * 2 + d.modifier
                          : d.total * d.critMult;
                      }
                    }
                    return (
                      <p key={di} className="text-[11px] font-mono text-parchment-muted/60">
                        {hasDice ? `${d.diceExpr.replace(/[+-].*/, '')} ${rollStr}` : ''}{modPart} = {total}
                        {d.notes ? <span className="text-parchment-muted/40">  {d.notes}</span> : null}
                        {doMult && <span className="text-gold/60 ml-1">{is5e && entry.atk.critStyle === 'perfect' ? 'max + crit roll' : `×${is5e ? 2 : d.critMult}`}</span>}
                      </p>
                    );
                  })}
                </div>
              ))}
            </div>
          ))}

          {/* Summary — adapts label based on whether attacks have damage rows */}
          {(() => {
            const specialEntries = Object.entries(completed.specialDamageTotals ?? {}) as [SpecialDamageType, number][];
            const anyDamage = completed.resolvedAttacks.some((a) => a.damage.length > 0)
              || (completed.conditionalDamageResults ?? []).some((c) => c.triggered)
              || (completed.bonusAttackResults ?? []).some((b) => b.triggered && b.attackResults.length > 0)
              || bonusRoundHistory.some((r) => r.roundDamage > 0)
              || specialEntries.length > 0;
            return (
              <div className="bg-darkbg-tertiary rounded-lg p-3 text-center">
                {anyDamage ? (
                  <>
                    {completed.totalDamage > 0 && (
                      <p className="font-display text-gold text-sm">
                        {completed.totalDamage} {specialEntries.length > 0 ? 'HP ' : ''}damage
                      </p>
                    )}
                    {specialEntries.map(([type, amt]) => (
                      <p key={type} className="font-display text-amber-400 text-sm mt-0.5">
                        {amt} {SPECIAL_DAMAGE_LABELS[type]}
                      </p>
                    ))}
                    <p className="text-xs text-parchment-muted/60 mt-0.5">
                      {completed.hits} / {completed.totalAttacks} hits
                    </p>
                  </>
                ) : (
                  <>
                    <p className="font-display text-gold text-sm">
                      {completed.hits} / {completed.totalAttacks}
                    </p>
                    <p className="text-xs text-parchment-muted/60 mt-0.5">
                      check{completed.totalAttacks !== 1 ? 's' : ''} succeeded
                    </p>
                  </>
                )}
              </div>
            );
          })()}

          {/* Export buttons */}
          <div className="grid grid-cols-2 gap-2">
            <button onClick={() => handleExport('discord')} disabled={exporting}
              className="flex items-center justify-center gap-1.5 py-2 text-xs
                         border border-darkbg-border rounded hover:border-indigo-400/50
                         text-parchment-muted/60 hover:text-indigo-300 transition-colors disabled:opacity-50">
              {copied === 'discord'
                ? <><Check className="w-3.5 h-3.5 text-success" /> Copied!</>
                : exporting ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                : <><Copy className="w-3.5 h-3.5" /> Discord</>}
            </button>
            <button onClick={() => handleExport('plain')} disabled={exporting}
              className="flex items-center justify-center gap-1.5 py-2 text-xs
                         border border-darkbg-border rounded hover:border-parchment-muted/40
                         text-parchment-muted/60 hover:text-parchment transition-colors disabled:opacity-50">
              {copied === 'plain'
                ? <><Check className="w-3.5 h-3.5 text-success" /> Copied!</>
                : <><Copy className="w-3.5 h-3.5" /> Plain Text</>}
            </button>
          </div>

          <button onClick={() => { setPhase('idle'); setPending(null); setCompleted(null); setHitMask([]); }}
            className="w-full text-xs text-parchment-muted/40 hover:text-parchment transition-colors py-1">
            Roll again
          </button>
        </div>
      )}
    </div>
  );
}
